import { FC } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Preview as PreviewIcon,
  Publish as PublishIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { CutsheetQuestionnaire } from '../../types';

export interface SaveFooterProps {
  questionnaire: CutsheetQuestionnaire;
  isDirty: boolean;
  isSaving: boolean;
  /** Left offset (in px) applied by the sidebar/drawer. */
  marginLeft: number;
  /** Top offset (in px) applied by the app header. */
  marginTop: number;
  onSave: () => void;
  onSaveAndPublish: () => void;
  onOpenSettings: () => void;
  onOpenPreview: () => void;
}

/**
 * Sticky top bar that shows the questionnaire name/status and provides Save,
 * Publish, Settings, and Preview actions.
 * @param root0
 * @param root0.questionnaire
 * @param root0.isDirty
 * @param root0.isSaving
 * @param root0.marginLeft
 * @param root0.marginTop
 * @param root0.onSave
 * @param root0.onSaveAndPublish
 * @param root0.onOpenSettings
 * @param root0.onOpenPreview
 */
export const SaveFooter: FC<SaveFooterProps> = ({
  questionnaire,
  isDirty,
  isSaving,
  marginLeft,
  marginTop,
  onSave,
  onSaveAndPublish,
  onOpenSettings,
  onOpenPreview,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: marginTop,
        left: marginLeft,
        right: 0,
        zIndex: 'appBar',
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 1,
        px: 2,
        boxShadow: 3,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {/* Left actions */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Back to Cut Sheet Builder">
            <IconButton
              size="small"
              onClick={() => navigate('/processor/cutsheets-v2')}
            >
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={onOpenPreview}
            size="small"
            disabled={questionnaire.questions.length === 0}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={onOpenSettings}
            size="small"
          >
            Settings
          </Button>
        </Stack>

        {/* Centre — questionnaire name + status chips */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="overline" component="h2">
            {questionnaire.name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              size="small"
              label={
                questionnaire.status === 'published'
                  ? 'Published'
                  : questionnaire.status === 'archived'
                    ? 'Archived'
                    : 'Draft'
              }
              color={
                questionnaire.status === 'published'
                  ? 'success'
                  : questionnaire.status === 'archived'
                    ? 'warning'
                    : 'default'
              }
            />
            <Chip
              size="small"
              label={isDirty ? 'Unsaved Changes' : 'Up to Date'}
              color={isDirty ? 'warning' : 'success'}
            />
            <Typography variant="body2" color="text.secondary">
              {questionnaire.questions.length} questions
            </Typography>
          </Stack>
        </Box>

        {/* Right actions */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={onSave}
            disabled={!isDirty || isSaving}
          >
            Save
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PublishIcon />
              )
            }
            onClick={onSaveAndPublish}
            disabled={isSaving}
          >
            {isDirty ? 'Save & Publish' : 'Publish'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
